import * as moment from 'moment';
import { normalizeVideoWidth } from '@/common/video/videoUploadCore';
export function safeUrl(value) {
    if (typeof value !== 'string' || !value.trim())
        return '';
    try {
        var url = new URL(value.trim(), window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    }
    catch (error) {
        return '';
    }
}
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, function (char) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char;
    });
}
function decodeLegacy(value) {
    var text = String(value || '');
    // Older Commbook clients store an entity-encoded HTML body. Decode in an inert template.
    for (var pass = 0; pass < 2 && /&(?:lt|gt|amp|quot|nbsp|#\d+|#x[\da-f]+);/i.test(text); pass++) {
        var template = document.createElement('template');
        template.innerHTML = text;
        var decoded = template.content.textContent || '';
        // Do not strip actual formatting when the body is already HTML.
        if (/<[a-z][\s\S]*>/i.test(text))
            break;
        text = decoded;
    }
    return text;
}
// Only emit explicitly allowed markup and attributes; never render the server HTML directly.
export function messageHtml(value) {
    var template = document.createElement('template');
    template.innerHTML = decodeLegacy(value);
    var allowed = new Set([
        'p',
        'div',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        's',
        'strike',
        'ul',
        'ol',
        'li',
        'blockquote',
        'pre',
        'code',
        'span',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'a',
        'img',
        'video',
        'source'
    ]);
    var blocked = new Set([
        'script',
        'style',
        'iframe',
        'object',
        'embed',
        'svg',
        'math',
        'template'
    ]);
    var render = function (node) {
        if (node.nodeType === Node.TEXT_NODE)
            return escapeHtml(node.textContent || '');
        if (node.nodeType !== Node.ELEMENT_NODE)
            return '';
        var element = node;
        var tag = element.tagName.toLowerCase();
        if (blocked.has(tag))
            return '';
        var children = Array.from(element.childNodes).map(render).join('');
        if (!allowed.has(tag))
            return children;
        var attributes = '';
        if (tag === 'a') {
            var href = safeUrl(element.getAttribute('href'));
            if (href)
                attributes += " href=\"".concat(escapeHtml(href), "\" target=\"_blank\" rel=\"noopener noreferrer\"");
        }
        if (tag === 'img' || tag === 'video' || tag === 'source') {
            var src = safeUrl(element.getAttribute('src'));
            if (tag === 'video') {
                if (!src) {
                    var source = Array.from(element.querySelectorAll('source')).find(function (child) {
                        return safeUrl(child.getAttribute('src'));
                    });
                    if (source)
                        src = safeUrl(source.getAttribute('src'));
                }
                if (!src)
                    return '';
                var poster = safeUrl(element.getAttribute('poster'));
                var width_1 = normalizeVideoWidth(element.getAttribute('width') || element.style.width);
                var videoAttributes = " src=\"".concat(escapeHtml(src), "\"") +
                    (poster ? " poster=\"".concat(escapeHtml(poster), "\"") : '') +
                    (width_1 === 'auto' ? '' : " width=\"".concat(width_1.replace('px', ''), "\""));
                return "<video controls playsinline preload=\"none\"".concat(videoAttributes, "></video>");
            }
            if (!src)
                return '';
            if (tag === 'source')
                return "<source src=\"".concat(escapeHtml(src), "\">");
            var width = (element.getAttribute('width') || element.style.width).trim();
            var pixelWidth = /^(\d{1,4}(?:\.\d{1,6})?)(?:px)?$/.exec(width);
            var safeWidth = pixelWidth && Number(pixelWidth[1]) > 0 && Number(pixelWidth[1]) <= 2000
                ? " style=\"width:".concat(pixelWidth[1], "px\"")
                : '';
            return "<img src=\"".concat(escapeHtml(src), "\" alt=\"").concat(escapeHtml(element.getAttribute('alt') || ''), "\"").concat(safeWidth, " loading=\"lazy\">");
        }
        var styles = [];
        var align = element.style.textAlign;
        if (['left', 'right', 'center', 'justify'].includes(align)) {
            styles.push("text-align:".concat(align));
        }
        for (var _i = 0, _a = ['color', 'background-color']; _i < _a.length; _i++) {
            var property = _a[_i];
            var value_1 = element.style.getPropertyValue(property);
            if (/^(#[\da-f]{3,8}|[a-z]+|rgba?\([\d\s,.%]+\))$/i.test(value_1))
                styles.push("".concat(property, ":").concat(value_1));
        }
        var size = element.style.fontSize;
        if (/^\d{1,3}(\.\d+)?(px|pt|em|rem|%)$/.test(size))
            styles.push("font-size:".concat(size));
        var family = element.style.fontFamily;
        if (/^[\w\s,'"-]+$/.test(family))
            styles.push("font-family:".concat(family));
        var decoration = element.style.textDecoration;
        if (/^(underline|line-through|overline|none)( (underline|line-through|overline))*$/.test(decoration))
            styles.push("text-decoration:".concat(decoration));
        if (styles.length)
            attributes += " style=\"".concat(escapeHtml(styles.join(';')), "\"");
        return tag === 'br' ? '<br>' : "<".concat(tag).concat(attributes, ">").concat(children, "</").concat(tag, ">");
    };
    return Array.from(template.content.childNodes).map(render).join('');
}
export function messageText(value) {
    var template = document.createElement('template');
    template.innerHTML = messageHtml(value);
    template.content.querySelectorAll('p,div,br,li,tr,h1,h2,h3,h4,h5,h6').forEach(function (element) {
        element.appendChild(document.createTextNode(' '));
    });
    return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
}
// Keep this in step with GlobalCommbookReplyService.visibleText on the API.
export function replyVisibleText(value) {
    var template = document.createElement('template');
    template.innerHTML = value || '';
    var result = '';
    var blocks = new Set(['p', 'div', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
    var visit = function (node) {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent || '';
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE)
            return;
        var element = node;
        var tag = element.tagName.toLowerCase();
        if (['script', 'style', 'template'].includes(tag))
            return;
        if (tag === 'br') {
            result += '\n';
            return;
        }
        if (blocks.has(tag) && result && !result.endsWith('\n'))
            result += '\n';
        Array.from(element.childNodes).forEach(visit);
    };
    Array.from(template.content.childNodes).forEach(visit);
    return result;
}
export function replyCharacterCount(value) {
    var count = 0;
    for (var _i = 0, _a = replyVisibleText(value); _i < _a.length; _i++) {
        var character = _a[_i];
        var code = character.codePointAt(0) || 0;
        count +=
            (code >= 0x3400 && code <= 0x9fff) ||
                (code >= 0xf900 && code <= 0xfaff) ||
                (code >= 0x20000 && code <= 0x2fa1ff) ||
                (code >= 0xff01 && code <= 0xff60) ||
                (code >= 0xffe0 && code <= 0xffe6)
                ? 2
                : 1;
    }
    return count;
}
export function attachments(record) {
    if (Number(record.deleted) === 1)
        return [];
    var files = record.fileObject;
    if (typeof files === 'string') {
        try {
            files = JSON.parse(files);
        }
        catch (error) {
            files = [];
        }
    }
    if (!Array.isArray(files) || !files.length) {
        var legacy = Array.isArray(record.fileList)
            ? record.fileList
            : record.file
                ? [record.file]
                : [];
        files = legacy.map(function (src, index) { return ({
            src: src,
            fileName: (record.fileDisplayNameList || [])[index] || record.fileDisplayName
        }); });
    }
    return files.reduce(function (result, file) {
        if (!file || typeof file !== 'object')
            return result;
        var src = safeUrl(file.src);
        if (src) {
            result.push({
                src: src,
                fileName: file.fileName || src.split('/').pop().split('?')[0] || 'Attachment',
                size: Number(file.size) || undefined
            });
        }
        return result;
    }, []);
}
export function mediaType(file) {
    var name = "".concat(file.fileName, " ").concat(file.src.split('?')[0]);
    if (/\.(png|jpe?g|gif|webp|bmp)(?:\s|$)/i.test(name))
        return 'image';
    if (/\.(mp4|webm|mov)(?:\s|$)/i.test(name))
        return 'video';
    if (/\.(mp3|wav|m4a|ogg)(?:\s|$)/i.test(name))
        return 'audio';
    return 'file';
}
export function conversationPreview(record) {
    var files = attachments({ fileObject: record.latestFileObject });
    var hasLatest = record.latestMessage != null || !!record.latestFileObject;
    var source = hasLatest ? record.latestMessage : record.plainTextMessage || record.message;
    var text = messageText(source);
    var html = messageHtml(source);
    var kind = files.length
        ? mediaType(files[0])
        : /<video\b/i.test(html)
            ? 'video'
            : /<img\b/i.test(html)
                ? 'image'
                : '';
    var label = kind === 'image'
        ? 'Image'
        : kind === 'video'
            ? 'Video'
            : kind === 'audio'
                ? 'Audio'
                : files.map(function (file) { return file.fileName; }).join(', ');
    return {
        text: text || label || 'No message preview',
        icon: {
            image: 'el-icon-picture-outline',
            video: 'el-icon-video-camera',
            audio: 'el-icon-headset',
            file: 'el-icon-document'
        }[kind] || ''
    };
}
export function studentName(record) {
    return ([record.studentFirstName, record.studentLastName].filter(Boolean).join(' ') ||
        record.studentName ||
        'Student');
}
export function authorName(record, reply) {
    if (reply === void 0) { reply = false; }
    return ((reply
        ? [record.responseUserFirstname, record.responseUserLastname]
        : [record.pubUserFirstname, record.pubUserLastname])
        .filter(Boolean)
        .join(' ') ||
        (reply ? record.responseUsername : record.pubUsername) ||
        'Unknown author');
}
export function formatTime(value) {
    if (!value)
        return '';
    var date = moment.utc(value).local();
    return date.isValid() ? date.format('DD/MM/YYYY, hh:mm A') : '';
}
export function withinEditHour(record) {
    var value = record.modifyTime || record.createTime;
    if (!value)
        return false;
    var time = moment.utc(value);
    return time.isValid() && Date.now() - time.valueOf() <= 60 * 60 * 1000;
}
export function isUnread(record) {
    return record.isRead === 0 || record.isRead === '0';
}
